import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import api from '../api';
import { UserContext } from '../context/UserContext';

function clampInt(value, min, max) {
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n)) return min;
  return Math.max(min, Math.min(max, n));
}

function clamp01(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.min(1, n));
}

function formatTiming(score) {
  if (score === null || score === undefined) return '';
  const n = Number(score);
  if (!Number.isFinite(n)) return '';
  return `${Math.round(n * 100)}%`;
}

function mulberry32(seed) {
  let t = seed >>> 0;
  return () => {
    t += 0x6D2B79F5;
    let x = t;
    x = Math.imul(x ^ (x >>> 15), x | 1);
    x ^= x + Math.imul(x ^ (x >>> 7), x | 61);
    return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
  };
}

function seedFromString(s) {
  const str = String(s || '');
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export default function Greifautomat() {
  const { user } = useContext(UserContext);
  const isOwner = Array.isArray(user?.roles) && user.roles.includes('owner');

  const [items, setItems] = useState([]);
  const [layoutTokens, setLayoutTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [playCost, setPlayCost] = useState(0);
  const [editPlayCost, setEditPlayCost] = useState(0);
  const [costMsg, setCostMsg] = useState('');

  const [grabChance, setGrabChance] = useState(1);
  const [editGrabChancePct, setEditGrabChancePct] = useState(100);

  const [freeSpins, setFreeSpins] = useState({ daily: 0, bonus: 0, total: 0 });

  const [playing, setPlaying] = useState(false);
  const [phase, setPhase] = useState('idle'); // idle | dropping | grabbing | lifting | delivering
  const [result, setResult] = useState(null);
  const [pending, setPending] = useState(null);

  const [claiming, setClaiming] = useState(false);
  const [claimMsg, setClaimMsg] = useState('');
  const [claimErr, setClaimErr] = useState('');

  const [editItems, setEditItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [saveErr, setSaveErr] = useState('');

  const clawPosRef = useRef({ x: 0.5, y: 0.5 });
  const [clawX, setClawX] = useState(0.5);
  const [clawY, setClawY] = useState(0.5);
  const [lockedXY, setLockedXY] = useState(null);

  const timersRef = useRef([]);
  const didAutoClaimRef = useRef(false);

  const clearTimers = () => {
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current = [];
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        const [cfgRes, meRes] = await Promise.all([
          api.get('/claw-machine/config'),
          api.get('/claw-machine/me'),
        ]);

        const cfg = cfgRes.data;
        const me = meRes.data;
        if (!mounted) return;

        const list = Array.isArray(cfg?.items) ? cfg.items : [];
        const layout = Array.isArray(cfg?.layout) ? cfg.layout : [];
        const pc = Number(cfg?.playCost) || 0;
        const gc = Number(cfg?.grabChance);
        setItems(list);
        setLayoutTokens(layout);
        setEditItems(list.map((it) => ({ ...it })));
        setPlayCost(pc);
        setEditPlayCost(pc);

        if (Number.isFinite(gc)) {
          const clamped = Math.max(0, Math.min(1, gc));
          setGrabChance(clamped);
          setEditGrabChancePct(Math.round(clamped * 100));
        } else {
          setGrabChance(1);
          setEditGrabChancePct(100);
        }

        const fs = me?.freeSpins;
        setFreeSpins({
          daily: Number(fs?.daily) || 0,
          bonus: Number(fs?.bonus) || 0,
          total: Number(fs?.total) || 0,
        });
      } catch (e) {
        if (!mounted) return;
        setLoadError(e?.response?.data?.error || e.message || 'Fehler beim Laden');
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const nextPlayNotice = useMemo(() => {
    const total = Number(freeSpins?.total) || 0;
    const bonus = Number(freeSpins?.bonus) || 0;
    const cost = Number(playCost) || 0;
    if (total > 0) {
      return {
        headline: 'Nächster Versuch: GRATIS',
        detail: `Quelle: Bonus (Bonus: ${bonus})`,
      };
    }
    if (cost <= 0) return { headline: 'Nächster Versuch: GRATIS', detail: 'Preis: 0 Wochenpunkte' };
    return { headline: `Nächster Versuch: kostet ${cost} Wochenpunkte`, detail: '' };
  }, [freeSpins, playCost]);

  const legendEntries = useMemo(() => {
    const list = Array.isArray(items) ? items.slice(0, 10) : [];
    return list.map((it, i) => {
      const label = String(it?.label || `Symbol ${i + 1}`).trim() || `Symbol ${i + 1}`;
      const actionType = String(it?.actionType || 'none');
      if (actionType === 'points') {
        const n = Math.trunc(Number(it?.pointsDelta) || 0);
        const sign = n > 0 ? '+' : '';
        return { key: i, label, reward: `${sign}${n} Wochenpunkte` };
      }
      if (actionType === 'freeSpins') {
        const n = Math.max(0, Math.trunc(Number(it?.freeSpinsDelta) || 0));
        return { key: i, label, reward: `+${n} Gratisversuche` };
      }
      if (actionType === 'prize') {
        const prize = String(it?.prizeLabel || '').trim();
        return { key: i, label, reward: `Sachpreis: ${prize || '(ohne Name)'}` };
      }
      return { key: i, label, reward: 'Niete' };
    });
  }, [items]);

  useEffect(() => {
    clawPosRef.current = {
      x: clamp01(clawX) ?? 0.5,
      y: clamp01(clawY) ?? 0.5,
    };
  }, [clawX, clawY]);

  useEffect(() => () => {
    clearTimers();
  }, []);

  const claim = async (playId) => {
    if (!playId) return;
    setClaiming(true);
    setClaimMsg('');
    setClaimErr('');
    try {
      const { data } = await api.post('/claw-machine/claim', { playId });
      const action = data?.action;
      if (action?.type === 'points') {
        const n = Number(action?.pointsDelta) || 0;
        const sign = n > 0 ? '+' : '';
        setClaimMsg(`Punkte: ${sign}${n}`);
      } else if (action?.type === 'freeSpins') {
        const n = Number(action?.freeSpinsDelta) || 0;
        setClaimMsg(`Gratisversuche: +${n}`);
      } else if (action?.type === 'prize') {
        setClaimMsg(`Sachpreis gewonnen: ${action?.label || ''}`);
      } else {
        setClaimMsg('Niete');
      }

      const fs = data?.freeSpins;
      if (fs) {
        setFreeSpins({ daily: Number(fs?.daily) || 0, bonus: Number(fs?.bonus) || 0, total: Number(fs?.total) || 0 });
      }
    } catch (err) {
      setClaimErr(err?.response?.data?.error || err.message || 'Aktion fehlgeschlagen');
    } finally {
      setClaiming(false);
    }
  };

  const play = async () => {
    if (playing) return;
    if (!items.length) return;

    setSaveMsg('');
    setSaveErr('');
    setClaimMsg('');
    setClaimErr('');
    setCostMsg('');
    setResult(null);
    setPending(null);
    didAutoClaimRef.current = false;

    setPlaying(true);

    const posX = clamp01(clawPosRef.current?.x);
    const posY = clamp01(clawPosRef.current?.y);
    setLockedXY({ x: posX ?? 0.5, y: posY ?? 0.5 });

    try {
      const { data } = await api.post('/claw-machine/play', { timingX: posX, timingY: posY });

      const usedFree = typeof data?.usedFreeSpin === 'string' ? data.usedFreeSpin : null;
      const costApplied = Number(data?.costApplied) || 0;
      if (usedFree) setCostMsg('Gratisversuch verwendet');
      else if (costApplied > 0) setCostMsg(`Preis: -${costApplied} Wochenpunkte`);

      const fs = data?.freeSpins;
      if (fs) {
        setFreeSpins({ daily: Number(fs?.daily) || 0, bonus: Number(fs?.bonus) || 0, total: Number(fs?.total) || 0 });
      }

      const playId = typeof data?.playId === 'string' ? data.playId : '';
      const chosen = data?.chosen || null;
      const animation = data?.animation || {};
      const timing = data?.timing || {};

      if (timing?.didGrabAnything === false) {
        setCostMsg((prev) => (prev ? `${prev} · Leer gegriffen` : 'Leer gegriffen'));
      }

      setPending({
        playId,
        chosen,
        animation,
        timing,
      });

      const dropMs = clampInt(animation?.dropMs, 300, 3000);
      const grabMs = clampInt(animation?.grabMs, 300, 3000);
      const liftMs = clampInt(animation?.liftMs, 300, 3000);

      setPhase('dropping');
      timersRef.current.push(setTimeout(() => setPhase('grabbing'), dropMs));
      timersRef.current.push(setTimeout(() => setPhase('lifting'), dropMs + grabMs));
      timersRef.current.push(setTimeout(() => setPhase('delivering'), dropMs + grabMs + liftMs));
      timersRef.current.push(setTimeout(() => {
        setPhase('idle');
        const final = { playId, chosen, timing };
        setResult(final);
        setPending(null);

        if (!didAutoClaimRef.current) {
          didAutoClaimRef.current = true;
          claim(playId);
        }

        setLockedXY(null);
        setPlaying(false);
      }, dropMs + grabMs + liftMs + 900));
    } catch (e) {
      alert(e?.response?.data?.error || e.message || 'Versuch fehlgeschlagen');
      setPlaying(false);
      setPhase('idle');
      setLockedXY(null);
    }
  };

  const saveConfig = async () => {
    if (!isOwner) return;
    setSaving(true);
    setSaveMsg('');
    setSaveErr('');

    try {
      const gc01 = clamp01(Number(editGrabChancePct) / 100);
      const payload = {
        playCost: clampInt(editPlayCost, 0, 1000000),
        grabChance: gc01 === null ? 1 : gc01,
        items: (editItems || []).map((it, i) => ({
          label: String(it?.label || `Symbol ${i + 1}`).trim(),
          weight: clampInt(it?.weight, 0, 1000000),
          actionType: it?.actionType === 'points'
            ? 'points'
            : (it?.actionType === 'freeSpins'
              ? 'freeSpins'
              : (it?.actionType === 'prize' ? 'prize' : 'none')),
          pointsDelta: it?.actionType === 'points' ? clampInt(it?.pointsDelta, -1000000, 1000000) : null,
          freeSpinsDelta: it?.actionType === 'freeSpins' ? clampInt(it?.freeSpinsDelta, 0, 1000000) : null,
          prizeLabel: it?.actionType === 'prize' ? String(it?.prizeLabel || '').trim() : null,
        })),
      };

      const { data } = await api.put('/claw-machine/config', payload);
      const list = Array.isArray(data?.items) ? data.items : payload.items;
      const layout = Array.isArray(data?.layout) ? data.layout : null;
      const pc = Number(data?.playCost) || payload.playCost;
      const gc = Number(data?.grabChance);
      setItems(list);
      if (layout) setLayoutTokens(layout);
      setEditItems(list.map((it) => ({ ...it })));
      setPlayCost(pc);
      setEditPlayCost(pc);

      if (Number.isFinite(gc)) {
        const clamped = Math.max(0, Math.min(1, gc));
        setGrabChance(clamped);
        setEditGrabChancePct(Math.round(clamped * 100));
      } else {
        setGrabChance(payload.grabChance);
        setEditGrabChancePct(Math.round(payload.grabChance * 100));
      }

      setSaveMsg('Gespeichert');
    } catch (e) {
      setSaveErr(e?.response?.data?.error || e.message || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const chosenIsWin = (chosen) => {
    const t = String(chosen?.actionType || 'none');
    return t !== 'none';
  };

  const machinePrizes = useMemo(() => {
    const list = Array.isArray(items) ? items.slice(0, 10) : [];
    while (list.length < 10) list.push({ label: `Symbol ${list.length + 1}`, actionType: 'none' });
    return list;
  }, [items]);

  const scatterPrizes = useMemo(() => {
    const tokens = Array.isArray(layoutTokens) ? layoutTokens : [];
    if (tokens.length > 0) {
      return tokens
        .map((t, i) => {
          const itemIndex = Number(t?.itemIndex);
          const item = Number.isFinite(itemIndex) && itemIndex >= 0 && itemIndex < machinePrizes.length
            ? machinePrizes[itemIndex]
            : null;
          const label = String(item?.label || '').trim() || 'Unbekannt';
          const x = Number(t?.x);
          const y = Number(t?.y);
          const rot = Number(t?.rot);
          if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
          return {
            key: String(t?.id || `t${i}`),
            label,
            x,
            y,
            rot: Number.isFinite(rot) ? rot : 0,
          };
        })
        .filter(Boolean);
    }

    // Fallback (should rarely happen): local deterministic scatter.
    return machinePrizes.map((it, i) => {
      const label = String(it?.label || `Symbol ${i + 1}`).trim() || `Symbol ${i + 1}`;
      const seed = seedFromString(`${label}:${i}`);
      const rnd = mulberry32(seed);
      const x = 0.08 + rnd() * 0.84;
      const y = 0.10 + rnd() * 0.86;
      const rot = (rnd() - 0.5) * 10;
      return { key: i, label, x, y, rot };
    });
  }, [layoutTokens, machinePrizes]);

  const activeX = lockedXY !== null ? lockedXY.x : clawX;
  const activeY = lockedXY !== null ? lockedXY.y : clawY;
  const y01 = Number.isFinite(activeY) ? activeY : 0.5;
  const yOffset = Math.max(-46, Math.min(46, Math.round((y01 - 0.5) * 92)));
  const baseTop = 10;

  const clawStyle = {
    position: 'absolute',
    left: `${Math.round(activeX * 1000) / 10}%`,
    transform: 'translateX(-50%)',
    top: baseTop,
    transition: playing ? 'left 80ms linear' : 'left 80ms linear',
    pointerEvents: 'none',
    zIndex: 3,
  };

  const armStyle = {
    position: 'absolute',
    left: '50%',
    top: 0,
    transform: 'translateX(-50%)',
    width: 3,
    height: phase === 'dropping' ? (170 + yOffset) : (phase === 'grabbing' ? (190 + yOffset) : 40),
    background: 'rgba(255,255,255,0.7)',
    borderRadius: 2,
    transition: 'height 900ms ease',
  };

  const clawBodyStyle = {
    position: 'absolute',
    left: '50%',
    top: phase === 'dropping' ? (165 + yOffset) : (phase === 'grabbing' ? (185 + yOffset) : 35),
    transform: 'translateX(-50%)',
    width: 46,
    height: 34,
    borderRadius: 10,
    background: 'rgba(0,0,0,0.45)',
    border: '1px solid rgba(255,255,255,0.25)',
    boxShadow: '0 10px 18px rgba(0,0,0,0.35)',
    transition: 'top 900ms ease',
  };

  const clawGripStyle = {
    position: 'absolute',
    left: '50%',
    top: phase === 'dropping' ? (195 + yOffset) : (phase === 'grabbing' ? (215 + yOffset) : 60),
    transform: 'translateX(-50%)',
    width: 72,
    height: 20,
    display: 'flex',
    gap: 6,
    justifyContent: 'center',
    transition: 'top 900ms ease',
  };

  const fingerStyle = (side) => ({
    width: 18,
    height: 18,
    borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.25)',
    background: 'rgba(255,255,255,0.08)',
    transform: phase === 'grabbing'
      ? (side === 'left' ? 'rotate(20deg) translateX(8px)' : 'rotate(-20deg) translateX(-8px)')
      : (side === 'left' ? 'rotate(-10deg)' : 'rotate(10deg)'),
    transition: 'transform 700ms ease',
  });

  const carriedLabel = pending?.chosen?.label;
  const didGrabAnything = pending?.timing?.didGrabAnything;

  if (loading) {
    return (
      <div className="home-page">
        <section style={{ width: '100vw', margin: 0, padding: 0, position: 'relative', left: '-120px' }}>
          <h2 className="hero-title" style={{ textAlign: 'center', margin: '0 0 8px' }}>Greifautomat</h2>
          <p className="hero-desc" style={{ margin: '0 auto 24px', textAlign: 'center', maxWidth: 760 }}>Lade…</p>
        </section>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="home-page">
        <section style={{ width: '100vw', margin: 0, padding: 0, position: 'relative', left: '-120px' }}>
          <h2 className="hero-title" style={{ textAlign: 'center', margin: '0 0 8px' }}>Greifautomat</h2>
          <p className="hero-desc" style={{ margin: '0 auto 24px', textAlign: 'center', maxWidth: 760 }}>{loadError}</p>
        </section>
      </div>
    );
  }

  return (
    <div className="home-page">
      <section style={{ width: '100vw', margin: 0, padding: 0, position: 'relative', left: '-120px' }}>
        <h2 className="hero-title" style={{ textAlign: 'center', margin: '0 0 8px' }}>Greifautomat</h2>
        <p className="hero-desc" style={{ margin: '0 auto 24px', textAlign: 'center', maxWidth: 760 }}>
          Sweet-Spot: Mitte (X/Y) für bessere Chancen.
        </p>
      </section>

      <section
        style={{
          width: '100%',
          maxWidth: 980,
          margin: '0 auto',
          padding: '0 16px 50px 16px',
          color: '#fff',
        }}
      >
        <div
          style={{
            margin: '0 0 14px',
            padding: 14,
            borderRadius: 14,
            border: '1px solid rgba(255,255,255,0.18)',
            background: 'rgba(0,0,0,0.35)',
            boxShadow: '0 10px 24px rgba(0,0,0,0.35)',
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: 8,
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 900 }}>
            {nextPlayNotice.headline}
          </div>
          {nextPlayNotice.detail ? (
            <div style={{ fontSize: 12, color: '#ccc' }}>{nextPlayNotice.detail}</div>
          ) : null}
          {costMsg ? (
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.92)' }}>{costMsg}</div>
          ) : null}
          {claimMsg ? (
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.92)' }}>{claimMsg}</div>
          ) : null}
          {claimErr ? (
            <div style={{ fontSize: 12, color: '#ff9b9b' }}>{claimErr}</div>
          ) : null}
        </div>

        <div
          style={{
            borderRadius: 16,
            border: '1px solid rgba(255,255,255,0.18)',
            background: 'rgba(0,0,0,0.35)',
            boxShadow: '0 10px 24px rgba(0,0,0,0.35)',
            padding: 14,
          }}
        >
          <div
            style={{
              position: 'relative',
              borderRadius: 14,
              border: '1px solid rgba(255,255,255,0.14)',
              background: 'rgba(10,10,10,0.55)',
              height: 320,
              overflow: 'hidden',
            }}
          >
            {/* prizes */}
            <div
              style={{
                position: 'absolute',
                left: 12,
                right: 12,
                bottom: 12,
                top: 80,
                opacity: 0.95,
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  perspective: 900,
                  transformStyle: 'preserve-3d',
                }}
              >
                {/* floor plane */}
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    transformStyle: 'preserve-3d',
                    transform: 'rotateX(58deg)',
                    transformOrigin: '50% 100%',
                    borderRadius: 14,
                  }}
                >
                  {/* floor shading */}
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      borderRadius: 14,
                      background: 'linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.00))',
                      border: '1px solid rgba(255,255,255,0.08)',
                    }}
                  />

                  {/* target / shadow marker on floor */}
                  <div
                    style={{
                      position: 'absolute',
                      left: `${Math.round(Number(activeX) * 1000) / 10}%`,
                      top: `${Math.round(Number(activeY) * 1000) / 10}%`,
                      transform: 'translate(-50%, -50%)',
                      width: 26,
                      height: 26,
                      borderRadius: 999,
                      border: '2px solid rgba(255,255,255,0.28)',
                      background: 'rgba(0,0,0,0.18)',
                      pointerEvents: 'none',
                    }}
                    title="Zielposition"
                  />

                  {scatterPrizes.map((p) => {
                    const scale = 0.85 + 0.35 * Math.max(0, Math.min(1, Number(p.y) || 0));
                    return (
                      <div
                        key={p.key}
                        style={{
                          position: 'absolute',
                          left: `${Math.round(p.x * 1000) / 10}%`,
                          top: `${Math.round(p.y * 1000) / 10}%`,
                          transform: `translate(-50%, -50%) rotate(${Math.round(p.rot * 10) / 10}deg) scale(${Math.round(scale * 100) / 100})`,
                          borderRadius: 12,
                          border: '1px solid rgba(255,255,255,0.14)',
                          background: 'rgba(255,255,255,0.06)',
                          width: 150,
                          height: 58,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 900,
                          letterSpacing: 0.2,
                          padding: '0 10px',
                          textAlign: 'center',
                        }}
                        title={p.label}
                      >
                        {p.label}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* claw */}
            <div style={clawStyle}>
              <div style={armStyle} />
              <div style={clawBodyStyle} />
              <div style={clawGripStyle}>
                <div style={fingerStyle('left')} />
                <div style={fingerStyle('right')} />
              </div>

              {/* carry indicator */}
              {phase === 'lifting' || phase === 'delivering' ? (
                didGrabAnything === false ? (
                  <div
                    style={{
                      position: 'absolute',
                      left: '50%',
                      top: 235,
                      transform: 'translateX(-50%)',
                      padding: '6px 10px',
                      borderRadius: 12,
                      background: 'rgba(0,0,0,0.55)',
                      border: '1px solid rgba(255,255,255,0.18)',
                      fontSize: 12,
                      fontWeight: 900,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Leer gegriffen
                  </div>
                ) : chosenIsWin(pending?.chosen) ? (
                  <div
                    style={{
                      position: 'absolute',
                      left: '50%',
                      top: 235,
                      transform: 'translateX(-50%)',
                      padding: '6px 10px',
                      borderRadius: 12,
                      background: 'rgba(0,0,0,0.55)',
                      border: '1px solid rgba(255,255,255,0.18)',
                      fontSize: 12,
                      fontWeight: 900,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {String(carriedLabel || 'Gewinn')}
                  </div>
                ) : null
              ) : null}
            </div>

            {/* top glass */}
            <div
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: 0,
                height: 70,
                background: 'linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0.00))',
                borderBottom: '1px solid rgba(255,255,255,0.10)',
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 12, flexWrap: 'wrap' }}>
            <button
              className="btn btn-primary"
              onClick={play}
              disabled={playing || claiming}
              style={{ minWidth: 220 }}
            >
              {playing ? 'Greift…' : 'Greifen'}
            </button>
            <div style={{ fontSize: 12, color: '#ccc' }}>
              Sweet-Spot: Mitte · X/Y: {lockedXY !== null ? 'fixiert' : 'läuft'}
              {pending?.timing?.score !== undefined && pending?.timing?.score !== null ? <> · Score: {formatTiming(pending.timing.score)}</> : null}
              {Number.isFinite(Number(grabChance)) ? <> · Greifchance: {Math.round(Number(grabChance) * 100)}%</> : null}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <div style={{ fontSize: 12, color: '#ccc', width: 22 }}>X</div>
              <input
                type="range"
                min={0}
                max={100}
                step={1}
                value={Math.round((Number(clawX) || 0) * 100)}
                disabled={playing || claiming || lockedXY !== null}
                onChange={(e) => setClawX(Math.max(0, Math.min(1, Number(e.target.value) / 100)))}
                style={{ width: 220 }}
              />
              <div style={{ fontSize: 12, color: '#ccc', width: 44 }}>{Math.round((Number(clawX) || 0) * 100)}%</div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <div style={{ fontSize: 12, color: '#ccc', width: 22 }}>Y</div>
              <input
                type="range"
                min={0}
                max={100}
                step={1}
                value={Math.round((Number(clawY) || 0) * 100)}
                disabled={playing || claiming || lockedXY !== null}
                onChange={(e) => setClawY(Math.max(0, Math.min(1, Number(e.target.value) / 100)))}
                style={{ width: 220 }}
              />
              <div style={{ fontSize: 12, color: '#ccc', width: 44 }}>{Math.round((Number(clawY) || 0) * 100)}%</div>
            </div>
          </div>

          {result?.chosen ? (
            <div style={{ marginTop: 10, fontSize: 12, color: 'rgba(255,255,255,0.92)' }}>
              Ergebnis: <strong style={{ fontWeight: 900 }}>{result?.timing?.didGrabAnything === false ? 'Leer gegriffen' : String(result.chosen.label || '')}</strong>
            </div>
          ) : null}

          <div
            style={{
              marginTop: 14,
              paddingTop: 12,
              borderTop: '1px solid rgba(255,255,255,0.14)',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: 10,
            }}
          >
            {legendEntries.map((e) => (
              <div
                key={e.key}
                style={{
                  borderRadius: 12,
                  border: '1px solid rgba(255,255,255,0.14)',
                  background: 'rgba(255,255,255,0.05)',
                  padding: 10,
                }}
              >
                <div style={{ fontWeight: 900, marginBottom: 4 }}>{e.label}</div>
                <div style={{ fontSize: 12, color: '#ccc' }}>{e.reward}</div>
              </div>
            ))}
          </div>
        </div>

        {isOwner ? (
          <div
            style={{
              marginTop: 16,
              borderRadius: 16,
              border: '1px solid rgba(255,255,255,0.18)',
              background: 'rgba(0,0,0,0.35)',
              boxShadow: '0 10px 24px rgba(0,0,0,0.35)',
              padding: 14,
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 900, marginBottom: 10 }}>Owner-Konfiguration</div>

            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
              <label style={{ fontSize: 12, color: '#ccc' }}>Preis pro Versuch (Wochenpunkte)</label>
              <input
                value={editPlayCost}
                onChange={(e) => setEditPlayCost(e.target.value)}
                type="number"
                style={{ width: 160 }}
              />

              <label style={{ fontSize: 12, color: '#ccc' }}>Greifchance (%)</label>
              <input
                value={editGrabChancePct}
                onChange={(e) => setEditGrabChancePct(e.target.value)}
                type="number"
                min={0}
                max={100}
                style={{ width: 120 }}
              />
              <button className="btn" onClick={saveConfig} disabled={saving}>
                {saving ? 'Speichert…' : 'Speichern'}
              </button>
              {saveMsg ? <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.92)' }}>{saveMsg}</div> : null}
              {saveErr ? <div style={{ fontSize: 12, color: '#ff9b9b' }}>{saveErr}</div> : null}
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: 8, fontSize: 12, color: '#ccc' }}>Label</th>
                    <th style={{ textAlign: 'left', padding: 8, fontSize: 12, color: '#ccc' }}>Gewicht</th>
                    <th style={{ textAlign: 'left', padding: 8, fontSize: 12, color: '#ccc' }}>Typ</th>
                    <th style={{ textAlign: 'left', padding: 8, fontSize: 12, color: '#ccc' }}>Punkte</th>
                    <th style={{ textAlign: 'left', padding: 8, fontSize: 12, color: '#ccc' }}>Gratis</th>
                    <th style={{ textAlign: 'left', padding: 8, fontSize: 12, color: '#ccc' }}>Sachpreis-Name</th>
                  </tr>
                </thead>
                <tbody>
                  {(editItems || []).map((it, i) => (
                    <tr key={i}>
                      <td style={{ padding: 8 }}>
                        <input
                          value={it?.label ?? ''}
                          onChange={(e) => {
                            const next = [...editItems];
                            next[i] = { ...next[i], label: e.target.value };
                            setEditItems(next);
                          }}
                          style={{ width: 180 }}
                        />
                      </td>
                      <td style={{ padding: 8 }}>
                        <input
                          value={it?.weight ?? 0}
                          onChange={(e) => {
                            const next = [...editItems];
                            next[i] = { ...next[i], weight: e.target.value };
                            setEditItems(next);
                          }}
                          type="number"
                          style={{ width: 110 }}
                        />
                      </td>
                      <td style={{ padding: 8 }}>
                        <select
                          value={it?.actionType ?? 'none'}
                          onChange={(e) => {
                            const v = e.target.value;
                            const next = [...editItems];
                            next[i] = { ...next[i], actionType: v };
                            setEditItems(next);
                          }}
                        >
                          <option value="none">Niete</option>
                          <option value="points">Wochenpunkte</option>
                          <option value="freeSpins">Gratisversuche</option>
                          <option value="prize">Sachpreis</option>
                        </select>
                      </td>
                      <td style={{ padding: 8 }}>
                        <input
                          value={it?.pointsDelta ?? 0}
                          onChange={(e) => {
                            const next = [...editItems];
                            next[i] = { ...next[i], pointsDelta: e.target.value };
                            setEditItems(next);
                          }}
                          type="number"
                          style={{ width: 110 }}
                          disabled={String(it?.actionType) !== 'points'}
                        />
                      </td>
                      <td style={{ padding: 8 }}>
                        <input
                          value={it?.freeSpinsDelta ?? 0}
                          onChange={(e) => {
                            const next = [...editItems];
                            next[i] = { ...next[i], freeSpinsDelta: e.target.value };
                            setEditItems(next);
                          }}
                          type="number"
                          style={{ width: 110 }}
                          disabled={String(it?.actionType) !== 'freeSpins'}
                        />
                      </td>
                      <td style={{ padding: 8 }}>
                        <input
                          value={it?.prizeLabel ?? ''}
                          onChange={(e) => {
                            const next = [...editItems];
                            next[i] = { ...next[i], prizeLabel: e.target.value };
                            setEditItems(next);
                          }}
                          style={{ width: 220 }}
                          disabled={String(it?.actionType) !== 'prize'}
                          placeholder="z.B. Hoodie, Gutschein…"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
}
